import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from '@mui/material';
import {
  Save,
  Publish,
  Add,
  Preview,
  ArrowBack,
} from '@mui/icons-material';
import quizAPI from '../../services/quizAPI';
import { categoryAPI } from '../../services/api';
import QuestionEditor from '../../components/Quiz/QuestionEditor';
import { toast } from 'react-hot-toast';

const QuizForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    duration: 15,
    passingScore: 70,
    maxAttempts: 3,
    questionOrder: 'sequential',
    showCorrectAnswers: true,
    showExplanations: true,
    allowReview: true,
    questions: [],
    tags: [],
    difficulty: 'beginner',
    category: '',
  });

  const [categories, setCategories] = useState([]);

  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');

  const steps = ['Informations générales', 'Questions', 'Paramètres'];

  // Charger les catégories
  useEffect(() => {
    fetchCategories();
  }, []);

  // Charger le quiz en mode édition
  useEffect(() => {
    if (mode === 'edit' && id) {
      loadQuiz();
    }
  }, [mode, id]);

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      const categoriesList = response.data.categories || [];
      setCategories(categoriesList);
      
      // Si en mode création et aucune catégorie sélectionnée, sélectionner "Général" par défaut
      if (mode === 'create' && !formData.category && categoriesList.length > 0) {
        const generalCategory = categoriesList.find(cat => cat.name === 'Général');
        if (generalCategory) {
          setFormData(prev => ({ ...prev, category: generalCategory.id }));
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
    }
  };

  const loadQuiz = async () => {
    try {
      setLoading(true);
      console.log('📖 Loading quiz:', id);
      const response = await quizAPI.getById(id);
      console.log('✅ Quiz loaded:', response);
      const quiz = response.quiz || response.data?.quiz;
      
      setFormData({
        title: quiz.title,
        description: quiz.description,
        instructions: quiz.instructions || '',
        duration: quiz.duration,
        passingScore: quiz.passingScore,
        maxAttempts: quiz.maxAttempts,
        questionOrder: quiz.questionOrder,
        showCorrectAnswers: quiz.showCorrectAnswers,
        showExplanations: quiz.showExplanations,
        allowReview: quiz.allowReview,
        questions: quiz.questions.map(q => ({
          type: q.type,
          question: q.question,
          description: q.description || '',
          options: q.options || [],
          explanation: q.explanation || '',
          points: q.points || 1,
          media: q.media || { type: null, url: '', filename: '', size: 0 },
          id: q._id,
        })),
        tags: quiz.tags || [],
        difficulty: quiz.difficulty,
        category: quiz.category?._id || quiz.category || '',
      });
    } catch (error) {
      console.error('Erreur lors du chargement du quiz:', error);
      toast.error('Impossible de charger le quiz');
      navigate('/quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Supprimer l'erreur si le champ est maintenant rempli
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      type: 'single-choice',
      question: '',
      description: '',
      options: [
        { id: 'a', text: '', isCorrect: false },
        { id: 'b', text: '', isCorrect: false },
      ],
      explanation: '',
      points: 1,
    };
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion],
    });
  };

  const handleUpdateQuestion = (questionIndex, updatedQuestion) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex] = updatedQuestion;
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleDeleteQuestion = (questionIndex) => {
    const newQuestions = formData.questions.filter((_, i) => i !== questionIndex);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleMoveQuestionUp = (questionIndex) => {
    if (questionIndex === 0) return;
    const newQuestions = [...formData.questions];
    [newQuestions[questionIndex - 1], newQuestions[questionIndex]] = 
    [newQuestions[questionIndex], newQuestions[questionIndex - 1]];
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleMoveQuestionDown = (questionIndex) => {
    if (questionIndex === formData.questions.length - 1) return;
    const newQuestions = [...formData.questions];
    [newQuestions[questionIndex], newQuestions[questionIndex + 1]] = 
    [newQuestions[questionIndex + 1], newQuestions[questionIndex]];
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToDelete),
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    if (formData.duration < 1 || formData.duration > 180) {
      newErrors.duration = 'La durée doit être entre 1 et 180 minutes';
    }

    if (formData.passingScore < 0 || formData.passingScore > 100) {
      newErrors.passingScore = 'Le score de passage doit être entre 0 et 100%';
    }

    if (formData.questions.length === 0) {
      newErrors.questions = 'Au moins une question est requise';
    }

    // Valider chaque question
    formData.questions.forEach((q, index) => {
      if (!q.question.trim()) {
        newErrors[`question_${index}`] = 'Le texte de la question est requis';
      }

      if (q.type !== 'open-text') {
        const hasCorrectAnswer = q.options.some(opt => opt.isCorrect);
        if (!hasCorrectAnswer) {
          newErrors[`question_${index}_correct`] = 'Au moins une réponse correcte est requise';
        }

        const hasEmptyOption = q.options.some(opt => !opt.text.trim());
        if (hasEmptyOption) {
          newErrors[`question_${index}_options`] = 'Toutes les options doivent avoir un texte';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (publish = false) => {
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    try {
      setSaving(true);

      const quizData = {
        ...formData,
        status: publish ? 'published' : 'draft',
      };

      let response;
      if (mode === 'edit' && id) {
        response = await quizAPI.update(id, quizData);
        toast.success('Quiz modifié avec succès');
      } else {
        response = await quizAPI.create(quizData);
        toast.success('Quiz créé avec succès');
      }

      if (publish) {
        toast.success('Quiz publié !');
      }

      navigate('/quiz');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde du quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/quiz')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4">
            {mode === 'edit' ? 'Modifier le Quiz' : 'Créer un Quiz'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Publish />}
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            {mode === 'edit' ? 'Mettre à jour et Publier' : 'Créer et Publier'}
          </Button>
        </Box>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Étape 1 : Informations générales */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Informations générales
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Titre du quiz"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description || 'Décrivez brièvement le contenu du quiz'}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions pour les étudiants"
                multiline
                rows={3}
                value={formData.instructions}
                onChange={(e) => handleChange('instructions', e.target.value)}
                helperText="Instructions affichées avant de commencer le quiz"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="category-label">Catégorie</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  value={formData.category}
                  label="Catégorie"
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  <MenuItem value="">
                    <em>Général (par défaut)</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: category.color || '#1976d2'
                          }}
                        />
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Durée (minutes)"
                type="number"
                value={formData.duration}
                onChange={(e) => handleChange('duration', parseInt(e.target.value) || 0)}
                error={!!errors.duration}
                helperText={errors.duration}
                inputProps={{ min: 1, max: 180 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Score de passage (%)"
                type="number"
                value={formData.passingScore}
                onChange={(e) => handleChange('passingScore', parseInt(e.target.value) || 0)}
                error={!!errors.passingScore}
                helperText={errors.passingScore}
                inputProps={{ min: 0, max: 100 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Nombre de tentatives autorisées"
                type="number"
                value={formData.maxAttempts}
                onChange={(e) => handleChange('maxAttempts', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 10 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ordre des questions</InputLabel>
                <Select
                  value={formData.questionOrder}
                  label="Ordre des questions"
                  onChange={(e) => handleChange('questionOrder', e.target.value)}
                >
                  <MenuItem value="sequential">Séquentiel</MenuItem>
                  <MenuItem value="random">Aléatoire</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Niveau de difficulté</InputLabel>
                <Select
                  value={formData.difficulty}
                  label="Niveau de difficulté"
                  onChange={(e) => handleChange('difficulty', e.target.value)}
                >
                  <MenuItem value="beginner">Débutant</MenuItem>
                  <MenuItem value="intermediate">Intermédiaire</MenuItem>
                  <MenuItem value="advanced">Avancé</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Ajouter un tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button onClick={handleAddTag} variant="outlined" size="small">
                    Ajouter
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="contained" onClick={handleNext}>
              Suivant : Questions
            </Button>
          </Box>
        </Paper>
      )}

      {/* Étape 2 : Questions */}
      {activeStep === 1 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Questions ({formData.questions.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddQuestion}
              >
                Ajouter une question
              </Button>
            </Box>

            {errors.questions && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.questions}
              </Alert>
            )}

            {formData.questions.length === 0 ? (
              <Alert severity="info">
                Aucune question ajoutée. Cliquez sur "Ajouter une question" pour commencer.
              </Alert>
            ) : (
              formData.questions.map((question, index) => (
                <QuestionEditor
                  key={question.id}
                  question={question}
                  index={index}
                  onUpdate={handleUpdateQuestion}
                  onDelete={handleDeleteQuestion}
                  onMoveUp={handleMoveQuestionUp}
                  onMoveDown={handleMoveQuestionDown}
                  isFirst={index === 0}
                  isLast={index === formData.questions.length - 1}
                />
              ))
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Retour
            </Button>
            <Button variant="contained" onClick={handleNext}>
              Suivant : Paramètres
            </Button>
          </Box>
        </Box>
      )}

      {/* Étape 3 : Paramètres avancés */}
      {activeStep === 2 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Paramètres avancés
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showCorrectAnswers}
                      onChange={(e) => handleChange('showCorrectAnswers', e.target.checked)}
                    />
                  }
                  label="Afficher les réponses correctes après soumission"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showExplanations}
                      onChange={(e) => handleChange('showExplanations', e.target.checked)}
                    />
                  }
                  label="Afficher les explications après soumission"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowReview}
                      onChange={(e) => handleChange('allowReview', e.target.checked)}
                    />
                  }
                  label="Permettre de revoir les réponses avant soumission"
                />
              </Grid>
            </Grid>

            {/* Résumé */}
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Résumé du Quiz
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Titre</Typography>
                  <Typography variant="body1">{formData.title || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Durée</Typography>
                  <Typography variant="body1">{formData.duration} minutes</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Questions</Typography>
                  <Typography variant="body1">{formData.questions.length}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Score de passage</Typography>
                  <Typography variant="body1">{formData.passingScore}%</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Tags</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {formData.tags.length > 0 ? formData.tags.map(tag => (
                      <Chip key={tag} label={tag} size="small" />
                    )) : <Typography variant="body2">Aucun tag</Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleBack}>
              Retour
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={() => handleSave(false)}
                disabled={saving}
              >
                Sauvegarder en brouillon
              </Button>
              <Button
                variant="contained"
                startIcon={<Publish />}
                onClick={() => handleSave(true)}
                disabled={saving}
              >
                {mode === 'edit' ? 'Mettre à jour et Publier' : 'Créer et Publier'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QuizForm;

